(() => {
  class GrowComparison extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.input = this.querySelector('input[type="range"]');
      this.frame = this.querySelector('.grow-rich__compare-frame');
      this.drag = null;
      if (!this.input || !this.frame) return;
      const options = { signal: this.controller.signal };
      const update = () => {
        const value = Math.max(0, Math.min(100, Number(this.input.value)));
        this.style.setProperty('--grow-compare-position', `${value}%`);
        this.input.setAttribute('aria-valuetext', (this.dataset.valueLabel || '').replace('[percent]', String(value)));
      };
      const move = clientX => {
        const bounds = this.drag?.bounds;
        if (!bounds?.width) return;
        this.input.value = String(Math.round(Math.max(0, Math.min(100, (clientX - bounds.left) / bounds.width * 100))));
        this.input.dispatchEvent(new Event('input', { bubbles: true }));
      };
      const finish = event => {
        if (event.pointerId !== this.drag?.id) return;
        if (event.type === 'lostpointercapture' && event.target !== this.frame) return;
        if (event.type === 'pointerup') {
          move(event.clientX);
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
        }
        const id = this.drag.id;
        this.drag = null;
        this.removeAttribute('data-dragging');
        if (this.frame.hasPointerCapture(id)) this.frame.releasePointerCapture(id);
      };
      this.input.addEventListener('input', update, options);
      this.frame.addEventListener('dragstart', event => event.preventDefault(), options);
      this.frame.addEventListener('pointerdown', event => {
        if (!event.isPrimary || event.button !== 0 || this.drag) return;
        this.drag = { id: event.pointerId, x: event.clientX, y: event.clientY, bounds: this.frame.getBoundingClientRect(), pending: event.pointerType === 'touch' };
        if (!this.drag.pending) {
          event.preventDefault();
          this.frame.setPointerCapture(event.pointerId);
          this.setAttribute('data-dragging', '');
          this.input.focus({ preventScroll: true });
          move(event.clientX);
        }
      }, options);
      this.frame.addEventListener('pointermove', event => {
        if (event.pointerId !== this.drag?.id) return;
        if (this.drag.pending) {
          const x = Math.abs(event.clientX - this.drag.x);
          const y = Math.abs(event.clientY - this.drag.y);
          if (Math.max(x, y) < 6) return;
          if (y > x) {
            finish(event);
            return;
          }
          this.drag.pending = false;
          this.frame.setPointerCapture(event.pointerId);
          this.setAttribute('data-dragging', '');
        }
        event.preventDefault();
        move(event.clientX);
      }, options);
      for (const type of ['pointerup', 'pointercancel', 'lostpointercapture']) this.frame.addEventListener(type, finish, options);
      this.setAttribute('data-enhanced', '');
      update();
    }
    disconnectedCallback() {
      this.controller?.abort();
      if (this.drag && this.frame?.hasPointerCapture(this.drag.id)) this.frame.releasePointerCapture(this.drag.id);
      this.drag = null;
      this.removeAttribute('data-dragging');
      this.removeAttribute('data-enhanced');
    }
  }

  class GrowDetailsTabs extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.nav = this.querySelector('[data-rich-tab-nav]');
      this.tabs = Array.from(this.querySelectorAll('[data-rich-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-rich-tab-panel]'));
      if (!this.nav || !this.tabs.length || this.tabs.length !== this.panels.length) return;
      this.nav.hidden = false;
      this.nav.setAttribute('role', 'tablist');
      this.tabs.forEach((tab, index) => {
        tab.setAttribute('role', 'tab');
        const panel = this.panels[index];
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', tab.id);
        panel.setAttribute('tabindex', '0');
        panel.querySelector('[data-rich-tab-heading]')?.setAttribute('hidden', '');
        tab.addEventListener('click', () => this.select(index), { signal: this.controller.signal });
        tab.addEventListener('keydown', event => {
          let next;
          if (event.key === 'ArrowRight') next = (index + 1) % this.tabs.length;
          if (event.key === 'ArrowLeft') next = (index - 1 + this.tabs.length) % this.tabs.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = this.tabs.length - 1;
          if (next === undefined) return;
          event.preventDefault();
          this.select(next);
          this.tabs[next].focus({ preventScroll: true });
          this.tabs[next].scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'auto' });
        }, { signal: this.controller.signal });
      });
      this.select(0);
      document.addEventListener('shopify:block:select', event => {
        const panel = event.target.closest?.('[data-rich-tab-panel]');
        const index = this.panels.indexOf(panel);
        if (index >= 0) this.select(index);
      }, { signal: this.controller.signal });
    }
    select(index) {
      this.tabs.forEach((tab, position) => {
        tab.setAttribute('aria-selected', String(position === index));
        tab.tabIndex = position === index ? 0 : -1;
        this.panels[position].hidden = position !== index;
      });
    }
    disconnectedCallback() { this.controller?.abort(); }
  }

  const fixedDeadline = value => {
    const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(Z|[+-]\d{2}:\d{2})$/.exec(value || '');
    if (!match) return NaN;
    const [year, month, day, hour, minute, second] = match.slice(1, 7).map(Number);
    const calendar = new Date(Date.UTC(year, month - 1, day, hour, minute, second));
    if (calendar.getUTCFullYear() !== year || calendar.getUTCMonth() !== month - 1 || calendar.getUTCDate() !== day || calendar.getUTCHours() !== hour || calendar.getUTCMinutes() !== minute || calendar.getUTCSeconds() !== second) return NaN;
    if (match[7] !== 'Z') {
      const offsetHours = Number(match[7].slice(1, 3));
      const offsetMinutes = Number(match[7].slice(4, 6));
      if (offsetHours > 14 || offsetMinutes > 59 || (offsetHours === 14 && offsetMinutes > 0)) return NaN;
    }
    return Date.parse(value);
  };

  class GrowOfferCountdown extends HTMLElement {
    connectedCallback() {
      clearInterval(this.interval);
      this.end = fixedDeadline(this.dataset.end);
      this.clock = this.querySelector('[data-rich-clock]');
      this.expired = this.querySelector('[data-rich-expired]');
      if (!Number.isFinite(this.end) || !this.clock || !this.expired) {
        if (window.Shopify?.designMode && this.expired) {
          this.clock && (this.clock.hidden = true);
          this.querySelector('.grow-rich__offer-deadline')?.setAttribute('hidden', '');
          this.expired.textContent = this.dataset.invalid || '';
          this.expired.hidden = false;
        } else this.hidden = true;
        return;
      }
      this.hidden = false;
      this.update();
      if (this.end > Date.now()) this.interval = setInterval(() => this.update(), 1000);
    }
    update() {
      const remaining = Math.max(0, Math.ceil((this.end - Date.now()) / 1000));
      if (!remaining) {
        clearInterval(this.interval);
        this.clock.hidden = true;
        this.expired.hidden = false;
        this.expired.textContent = this.dataset.expired || '';
        this.expired.setAttribute('role', 'status');
        return;
      }
      this.clock.hidden = false;
      this.expired.hidden = true;
      const values = {
        days: Math.floor(remaining / 86400),
        hours: Math.floor(remaining % 86400 / 3600),
        minutes: Math.floor(remaining % 3600 / 60),
        seconds: remaining % 60
      };
      Object.entries(values).forEach(([key, value]) => {
        const node = this.querySelector(`[data-rich-${key}]`);
        if (node) node.textContent = key === 'days' ? String(value) : String(value).padStart(2, '0');
      });
    }
    disconnectedCallback() { clearInterval(this.interval); }
  }

  for (const [name, element] of [['grow-comparison', GrowComparison], ['grow-details-tabs', GrowDetailsTabs], ['grow-offer-countdown', GrowOfferCountdown]]) {
    if (!customElements.get(name)) customElements.define(name, element);
  }

  document.addEventListener('click', event => {
    const link = event.target.closest?.('[data-rich-scroll-options]');
    if (!link || event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
    const info = Array.from(document.querySelectorAll('product-info')).find(node => node.dataset.productId === link.dataset.richScrollOptions);
    if (!info) return;
    const target = info.querySelector('grow-pack, variant-selects, [id^="ProductInfo-"]') || info;
    event.preventDefault();
    target.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
    const control = target.querySelector('input:not([type="hidden"]), select, button:not([disabled])');
    control?.focus({ preventScroll: true });
  });
  window.GrowRichMath = { fixedDeadline };
})();
